/**
 * Registry central de empilhamento (z-index) de modais.
 *
 * Problema que resolve: cada modal tinha o z-index "hardcoded" (a maioria em
 * 10040). Como os modais são teleportados para o <body>, quando dois modais
 * abriam com o MESMO z-index o empilhamento passava a depender da ordem do DOM
 * — e o modal aberto por último podia aparecer ATRÁS do anterior (ex.: o
 * preview de relatório aparecendo atrás do modal de configuração de template).
 *
 * Solução: um registry singleton (estado em variáveis de módulo) que calcula,
 * no momento da abertura, o próximo z-index como `topo_atual + GAP`. Assim,
 * "quem abre depois fica sempre à frente". Ao fechar, o token é liberado para
 * não vazar valores na pilha.
 *
 * Uso típico em um modal base:
 *
 *   import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
 *
 *   const tokenZIndex = ref(null)
 *   const zIndexReservado = ref(null)
 *   const zIndexEfetivo = computed(() => zIndexReservado.value ?? props.zIndex ?? BASE_ZINDEX)
 *
 *   // ao abrir:
 *   const { token, zIndex } = reservarZIndex(props.zIndex)
 *   tokenZIndex.value = token
 *   zIndexReservado.value = zIndex
 *
 *   // ao fechar:
 *   liberarZIndex(tokenZIndex.value)
 *   tokenZIndex.value = null
 *   zIndexReservado.value = null
 *
 * O parâmetro `valorExplicito` (override opcional por prop) funciona como PISO:
 * o z-index efetivo é `Math.max(auto, valorExplicito)`, garantindo que um modal
 * filho nunca fique atrás do pai mesmo quando o pai usou um valor manual.
 */

/** Camada base de qualquer modal (mesmo valor histórico do sistema). */
export const BASE_ZINDEX = 10040

/** Distância entre camadas de modais empilhados. */
export const GAP_ENTRE_MODAIS = 20

/** Pilha de tokens reservados (singleton de módulo). */
const pilha = []

/** Sequencial para identificar cada token de forma única. */
let seq = 0

/**
 * Reserva o próximo z-index da pilha.
 *
 * @param {number|null|undefined} valorExplicito - Override opcional (piso).
 * @returns {{ token: { id: number, z: number }, zIndex: number }}
 */
export function reservarZIndex (valorExplicito) {
  const topo = pilha.reduce((max, t) => Math.max(max, t.z), 0)
  const auto = topo === 0 ? BASE_ZINDEX : topo + GAP_ENTRE_MODAIS
  const explicito = typeof valorExplicito === 'number' && Number.isFinite(valorExplicito)
    ? valorExplicito
    : 0
  const z = Math.max(auto, explicito)
  const token = { id: ++seq, z }
  pilha.push(token)
  return { token, zIndex: z }
}

/**
 * Libera um token previamente reservado (ao fechar o modal).
 *
 * @param {{ id: number }|null|undefined} token
 */
export function liberarZIndex (token) {
  if (!token) return
  const idx = pilha.findIndex((t) => t.id === token.id)
  if (idx >= 0) pilha.splice(idx, 1)
}

/**
 * Retorna o maior z-index atualmente reservado (ou a base, se a pilha estiver vazia).
 *
 * @returns {number}
 */
export function zIndexTopoAtual () {
  return pilha.reduce((max, t) => Math.max(max, t.z), BASE_ZINDEX)
}
